import { useCallback } from 'react';

/**
 * Stableford Scoring Hook (Client-side)
 *
 * Provides instant Stableford calculation without hitting the backend.
 * Logic mirrors the backend stablefordService.js exactly.
 */

export function useStableford() {
  /**
   * Calculate strokes received on a hole
   */
  const calculateStrokesReceived = useCallback((handicap, strokeIndex) => {
    const baseStrokes = Math.floor(handicap / 18);
    const extraStroke = (handicap % 18) >= strokeIndex ? 1 : 0;
    return baseStrokes + extraStroke;
  }, []);

  /**
   * Calculate Stableford points for a hole
   */
  const calculatePoints = useCallback((strokes, par, strokeIndex, handicap) => {
    // Validate inputs
    if (strokes < 1 || strokes > 15) {
      throw new Error('Strokes must be between 1 and 15');
    }
    if (![3, 4, 5].includes(par)) {
      throw new Error('Par must be 3, 4, or 5');
    }
    if (strokeIndex < 1 || strokeIndex > 18) {
      throw new Error('Stroke index must be between 1 and 18');
    }
    if (handicap < 0) {
      throw new Error('Handicap must be 0 or greater');
    }

    // Calculate strokes received
    const strokesReceived = calculateStrokesReceived(handicap, strokeIndex);

    // Calculate net score
    const netScore = strokes - strokesReceived;

    // Calculate score relative to par
    const scoreToPar = netScore - par;

    // Calculate Stableford points
    let points = 0;
    if (scoreToPar <= -2) {
      points = 4; // Eagle or better
    } else if (scoreToPar === -1) {
      points = 3; // Birdie
    } else if (scoreToPar === 0) {
      points = 2; // Par
    } else if (scoreToPar === 1) {
      points = 1; // Bogey
    } else {
      points = 0; // Double bogey or worse
    }

    return {
      points,
      netScore,
      strokesReceived,
      scoreToPar
    };
  }, [calculateStrokesReceived]);

  /**
   * Get description of score
   */
  const getScoreDescription = useCallback((scoreToPar) => {
    if (scoreToPar <= -3) return 'Albatross';
    if (scoreToPar === -2) return 'Eagle';
    if (scoreToPar === -1) return 'Birdie';
    if (scoreToPar === 0) return 'Par';
    if (scoreToPar === 1) return 'Bogey';
    if (scoreToPar === 2) return 'Double Bogey';
    if (scoreToPar === 3) return 'Triple Bogey';
    return `+${scoreToPar}`;
  }, []);

  /**
   * Get color for score (for UI)
   */
  const getScoreColor = useCallback((scoreToPar) => {
    if (scoreToPar <= -2) return 'text-blue-600 bg-blue-50'; // Eagle+
    if (scoreToPar === -1) return 'text-green-600 bg-green-50'; // Birdie
    if (scoreToPar === 0) return 'text-gray-700 bg-white'; // Par
    if (scoreToPar === 1) return 'text-yellow-600 bg-yellow-50'; // Bogey
    return 'text-red-600 bg-red-50'; // Double+
  }, []);

  /**
   * Calculate total for multiple holes
   */
  const calculateRoundTotal = useCallback((holes, handicap) => {
    let totalPoints = 0;
    let totalStrokes = 0;
    let holesCompleted = 0;

    const holeResults = holes.map((hole) => {
      if (hole.strokes === null || hole.strokes === undefined) {
        return {
          holeNumber: hole.holeNumber,
          strokes: null,
          points: null,
          netScore: null,
          strokesReceived: null
        };
      }

      const result = calculatePoints(
        hole.strokes,
        hole.par,
        hole.strokeIndex,
        handicap
      );

      totalPoints += result.points;
      totalStrokes += hole.strokes;
      holesCompleted++;

      return {
        holeNumber: hole.holeNumber,
        strokes: hole.strokes,
        ...result
      };
    });

    return {
      totalPoints,
      totalStrokes,
      holesCompleted,
      holeResults
    };
  }, [calculatePoints]);

  return {
    calculatePoints,
    calculateStrokesReceived,
    getScoreDescription,
    getScoreColor,
    calculateRoundTotal
  };
}

export default useStableford;
